import { useState, useEffect } from 'react';
import api from './api';
import './AccountManagement.css';

const AUTH_LABELS = { 0: '접근제한', 1: '기본', 2: '관리자' };
const fmtDate = v => v ? v.replace('T', ' ') : '-';
const EMPTY_CREATE = { userId: '', password: '', userName: '', authLevel: 1 };

export default function AccountManagement() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);
  const [mode, setMode]         = useState('view'); // 'view' | 'create'

  const [editForm, setEditForm]     = useState({ userName: '', authLevel: 1, password: '' });
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);

  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState({ text: '', type: '' });

  useEffect(() => { fetchList(); }, []);

  async function fetchList() {
    try {
      const res = await api.get('/api/account/list');
      setAccounts(res.data);
    } catch {
      setMsg({ text: '계정 목록을 불러오지 못했습니다.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }

  function selectAccount(acc) {
    setSelected(acc);
    setEditForm({ userName: acc.userName, authLevel: acc.authLevel, password: '' });
    setMode('view');
    setMsg({ text: '', type: '' });
  }

  function openCreate() {
    setSelected(null);
    setCreateForm(EMPTY_CREATE);
    setMode('create');
    setMsg({ text: '', type: '' });
  }

  function setEdit(key, value) { setEditForm(f => ({ ...f, [key]: value })); }
  function setCreate(key, value) { setCreateForm(f => ({ ...f, [key]: value })); }

  async function handleSave() {
    if (!selected) return;
    setSaving(true);
    setMsg({ text: '', type: '' });
    const body = { userName: editForm.userName, authLevel: Number(editForm.authLevel) };
    if (editForm.password) body.password = editForm.password;
    try {
      const res = await api.put(`/api/account/${selected.userUid}`, body);
      if (res.data.success !== false) {
        setMsg({ text: '수정되었습니다.', type: 'success' });
        const updated = { ...selected, userName: editForm.userName, authLevel: Number(editForm.authLevel) };
        setAccounts(prev => prev.map(a => a.userUid === selected.userUid ? updated : a));
        setSelected(updated);
        setEditForm(f => ({ ...f, password: '' }));
      } else {
        setMsg({ text: res.data.message || '수정에 실패했습니다.', type: 'error' });
      }
    } catch (err) {
      setMsg({ text: err.response?.data?.message || '서버 오류가 발생했습니다.', type: 'error' });
    } finally {
      setSaving(false);
    }
  }

  async function handleCreate() {
    const { userId, password, userName, authLevel } = createForm;
    if (!userId.trim() || !password.trim() || !userName.trim()) {
      setMsg({ text: '아이디, 비밀번호, 이름은 필수입니다.', type: 'error' });
      return;
    }
    setSaving(true);
    setMsg({ text: '', type: '' });
    try {
      const res = await api.post('/api/account/create', {
        userId: userId.trim(),
        password,
        userName: userName.trim(),
        authLevel: Number(authLevel),
      });
      const data = res.data;
      if (data.success) {
        setMsg({ text: '계정이 생성되었습니다.', type: 'success' });
        await fetchList();
        setMode('view');
        setCreateForm(EMPTY_CREATE);
      } else {
        setMsg({ text: data.message || '생성에 실패했습니다.', type: 'error' });
      }
    } catch (err) {
      setMsg({ text: err.response?.data?.message || '서버 오류가 발생했습니다.', type: 'error' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="acm">
      {/* ── 계정 목록 ── */}
      <div className="acm-list">
        <div className="acm-list-hd">
          <span>계정 목록 {!loading && `(${accounts.length})`}</span>
          <button className="acm-create-btn" onClick={openCreate}>+ 계정 생성</button>
        </div>
        {loading ? (
          <div className="acm-placeholder">불러오는 중...</div>
        ) : accounts.length === 0 ? (
          <div className="acm-placeholder">계정이 없습니다.</div>
        ) : (
          <ul className="acm-items">
            {accounts.map(acc => (
              <li key={acc.userUid}>
                <button
                  className={`acm-item${selected?.userUid === acc.userUid && mode === 'view' ? ' active' : ''}`}
                  onClick={() => selectAccount(acc)}
                >
                  <div className="acm-item-top">
                    <span className="acm-item-name">{acc.userName}</span>
                    <span className={`acm-badge level-${acc.authLevel}`}>
                      {AUTH_LABELS[acc.authLevel] ?? acc.authLevel}
                    </span>
                  </div>
                  <div className="acm-item-id">{acc.userId}</div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ── 상세 / 수정 / 생성 ── */}
      <div className="acm-detail">
        {mode === 'create' ? (
          <div className="acm-form">
            <div className="acm-form-hd">계정 생성</div>
            <div className="acm-section-label">계정 정보</div>
            <div className="acm-row">
              <label>아이디 *</label>
              <input
                type="text"
                value={createForm.userId}
                onChange={e => setCreate('userId', e.target.value)}
                className="acm-input"
                autoFocus
              />
            </div>
            <div className="acm-row">
              <label>비밀번호 *</label>
              <input
                type="password"
                value={createForm.password}
                onChange={e => setCreate('password', e.target.value)}
                className="acm-input"
              />
            </div>
            <div className="acm-row">
              <label>이름 *</label>
              <input
                type="text"
                value={createForm.userName}
                onChange={e => setCreate('userName', e.target.value)}
                className="acm-input"
              />
            </div>
            <div className="acm-row">
              <label>권한</label>
              <select
                value={createForm.authLevel}
                onChange={e => setCreate('authLevel', e.target.value)}
                className="acm-input"
              >
                <option value={2}>관리자</option>
                <option value={1}>기본</option>
                <option value={0}>접근제한</option>
              </select>
            </div>

            {msg.text && (
              <p className={`acm-msg acm-msg-${msg.type}`}>{msg.text}</p>
            )}

            <div className="acm-btn-row">
              <button className="acm-save-btn" onClick={handleCreate} disabled={saving}>
                {saving ? '생성 중...' : '생성'}
              </button>
              <button className="acm-cancel-btn" onClick={() => { setMode('view'); setMsg({ text: '', type: '' }); }}>
                취소
              </button>
            </div>
          </div>
        ) : !selected ? (
          <div className="acm-placeholder">좌측 목록에서 계정을 선택하세요.</div>
        ) : (
          <div className="acm-form">
            <div className="acm-form-hd">{selected.userId} 계정 정보</div>

            <div className="acm-section-label">기본 정보</div>
            <div className="acm-row">
              <label>아이디</label>
              <input type="text" value={selected.userId} readOnly className="acm-input readonly" />
            </div>
            <div className="acm-row">
              <label>등록일</label>
              <input type="text" value={fmtDate(selected.regDate)} readOnly className="acm-input readonly" />
            </div>
            <div className="acm-row">
              <label>최근 로그인</label>
              <input type="text" value={fmtDate(selected.lastLoginDate)} readOnly className="acm-input readonly" />
            </div>

            <div className="acm-section-label">수정</div>
            <div className="acm-row">
              <label>이름</label>
              <input
                type="text"
                value={editForm.userName}
                onChange={e => setEdit('userName', e.target.value)}
                className="acm-input"
              />
            </div>
            <div className="acm-row">
              <label>권한</label>
              <select
                value={editForm.authLevel}
                onChange={e => setEdit('authLevel', e.target.value)}
                className="acm-input"
              >
                <option value={2}>관리자</option>
                <option value={1}>기본</option>
                <option value={0}>접근제한</option>
              </select>
            </div>
            <div className="acm-row">
              <label>새 비밀번호</label>
              <input
                type="password"
                value={editForm.password}
                onChange={e => setEdit('password', e.target.value)}
                placeholder="변경 시에만 입력"
                className="acm-input"
              />
            </div>

            {msg.text && (
              <p className={`acm-msg acm-msg-${msg.type}`}>{msg.text}</p>
            )}

            <button className="acm-save-btn" onClick={handleSave} disabled={saving}>
              {saving ? '저장 중...' : '수정'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
